/**
 * Lead-form Interactivity store + REST-submit handler.
 *
 * Plan 06-03 — LEAD-01/02/03. Mounted on the `<form id="carlo-lead-form">`
 * partial via `data-wp-interactive="carlo/lead-form"` +
 * `data-wp-on--submit="actions.submit"`.
 *
 * SSR handover (PHP→JS) — W7 lock-in:
 *   PHP-side enqueue calls
 *   `wp_localize_script('carlo-lead-form', 'CarloLeadForm', $config)` where
 *   $config = $captchaVerifier->frontendConfig() merged with REST URL +
 *   nonce. This module reads `window.CarloLeadForm.{provider,siteKey,
 *   restNonce,restUrl}` directly — NEVER sniffs DOM data-* for these
 *   load-bearing values.
 *
 * Pitfall guards:
 *   - Pitfall 3 — Captcha token fetched on-submit (recaptcha v3
 *     grecaptcha.execute), not on-page-load.
 *     18.08.2026, `08-REVIEW.md` GC-01 — das galt bis dahin AUSSCHLIESSLICH
 *     fuer reCAPTCHA v3. Bei hCaptcha zeichnet das fremde Skript sein Widget
 *     in das `div.h-captcha` aus `templates/partials/lead-form.php:246-248`
 *     und haengt die Antwort selbst als Feld `h-captcha-response` darunter;
 *     ein `captcha_token`-Feld rendert dieser Zweig gar nicht. Wer die
 *     Antwort nicht ausliest, schickt bei vollstaendig gepflegtem
 *     Schluesselpaar JEDE Anfrage ohne Token hinaus — `LeadsController.php:91`
 *     antwortet dann `422 captcha_required`. Beide Anbieterzweige liegen
 *     deshalb seit D-15 in `captchaTokenFor()` (unten), einer exportierten,
 *     reinen Funktion, die `tests/Unit/Frontend/LeadFormCaptchaTokenTest.ts`
 *     ohne DOM und ohne Netz prueft.
 *   - On 422 (captcha fail) → user-visible error.
 *   - On success → form replaced with thank-you message + DataLayer
 *     `carlo_conversion` event pushed (Phase-5 main.ts recordConversion
 *     helper REUSED via window.dataLayer push).
 */

import { store as registerStore } from '@wordpress/interactivity';

interface CarloLeadFormConfig {
  provider: 'none' | 'hcaptcha' | 'recaptcha_v3';
  siteKey: string;
  dsgvoNoticeText: string;
  restUrl: string;
  restNonce: string;
}

/**
 * 09.08.2026 — die Konfiguration kommt AM FORMULAR an, nicht über ein Global.
 *
 * Der Kopfkommentar oben beschrieb einen `wp_localize_script`-Aufruf auf dem
 * klassischen Handle `carlo-lead-form`. Den gab es nie: dieses Modul ist ein
 * Script-MODUL, und für Module gibt es kein localize. `window.CarloLeadForm`
 * war deshalb auf jeder Seite `undefined`, und `submit()` stieg wortlos aus
 * (`if (!form || !cfg) return;`) — selbst dort, wo der Store registriert war.
 *
 * Jetzt liest jede Formular-Instanz ihre eigenen Werte aus den eigenen
 * Attributen: REST-Wurzel und Captcha-Anbieter als `data-*`, der Nonce aus dem
 * ohnehin vorhandenen versteckten Feld. Damit funktionieren auch die ZWEI
 * Instanzen auf einer Detailseite (Blatt + eingebettet) unabhängig
 * voneinander. `window.CarloLeadForm` bleibt als Rückfall bestehen, falls es
 * jemand von außen setzt.
 */
function configFor(form: HTMLFormElement): CarloLeadFormConfig {
  const global = window.CarloLeadForm;
  const nonceField = form.querySelector<HTMLInputElement>('input[name="_wpnonce"]');
  const provider = (form.dataset.captchaProvider ??
    global?.provider ??
    'none') as CarloLeadFormConfig['provider'];

  return {
    provider,
    siteKey: form.dataset.captchaSiteKey ?? global?.siteKey ?? '',
    dsgvoNoticeText: global?.dsgvoNoticeText ?? '',
    restUrl: form.dataset.carloRestUrl ?? global?.restUrl ?? '/wp-json/carlo/v1',
    restNonce: nonceField?.value ?? global?.restNonce ?? '',
  };
}

declare global {
  interface Window {
    CarloLeadForm?: CarloLeadFormConfig;
    dataLayer?: Array<Record<string, unknown>>;
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

/**
 * 09.08.2026 — die Aktion bekommt das EVENT, kein Kontext-Objekt.
 *
 * Hier stand `type SubmitContext = { event: SubmitEvent; … }` und die Aktion
 * las `context.event`. Die Interactivity-API reicht Ereignis-Direktiven aber
 * das Event selbst als erstes Argument. `context.event` war damit `undefined`
 * und die erste Zeile warf
 *   TypeError: Cannot read properties of undefined (reading 'target')
 * — gemessen im Browser, nachdem das Modul überhaupt einmal geladen wurde.
 * Vorher fiel es nicht auf, weil der Store nie registriert war und die
 * Direktive gar nicht erst feuerte: zwei Fehler, die einander verdeckt haben.
 */

const NS = 'carlo/lead-form';

async function fetchRecaptchaToken(siteKey: string): Promise<string> {
  if (!window.grecaptcha) {
    return '';
  }
  return new Promise<string>((resolve) => {
    window.grecaptcha!.ready(() => {
      window
        .grecaptcha!.execute(siteKey, { action: 'lead_submit' })
        .then((token) => resolve(token))
        .catch(() => resolve(''));
    });
  });
}

/**
 * Der Selektor, unter dem das hCaptcha-Widget seine Antwort ablegt.
 *
 * Exportiert, damit der Spec ihn adressieren kann, statt die Zeichenkette zu
 * wiederholen — und damit ein Tippfehler darin genau EINE Stelle betrifft.
 */
export const HCAPTCHA_RESPONSE_SELECTOR = '[name="h-captcha-response"]';

/**
 * Die Tokenwahl fuer beide Anbieter an EINER Stelle — rein und pruefbar.
 *
 * 18.08.2026, D-15 / `08-REVIEW.md` GC-01. Vorher stand die Entscheidung als
 * `if (cfg.provider === 'recaptcha_v3' && cfg.siteKey)` mitten in `submit()`:
 * nicht pruefbar ohne echtes Dokument, und fuer hCaptcha schlicht nicht
 * vorhanden.
 *
 * Die drei Wege:
 *   - `recaptcha_v3` mit Site-Key → `grecaptcha.execute()` wie bisher. Ohne
 *     Site-Key wird die fremde API GAR NICHT erst befragt.
 *   - `hcaptcha` → der Wert des Feldes `h-captcha-response`, das das Widget
 *     selbst ins Formular haengt (`templates/partials/lead-form.php:246-248`).
 *     Hier steht ABSICHTLICH kein zweiter Site-Key-Vorbehalt: ohne gepflegten
 *     Site-Key rendert das Partial das Widget gar nicht erst, und
 *     `CaptchaVerifier::provider()` faellt seit D-13 ohnehin auf `none`
 *     zurueck. Eine dritte Wahrheit ueber denselben Zustand waere genau die
 *     Drift, gegen die Phase 08 antritt.
 *   - alles andere, `none` eingeschlossen → leere Zeichenkette.
 *
 * Der Wert wird bewusst UNGEPRUEFT weitergereicht (T-08-66): pruefen tut ihn
 * `HCaptchaAdapter` serverseitig gegen `api.hcaptcha.com`. Bleibt das Feld
 * leer — Skript blockiert, Adblocker —, ist die leere Zeichenkette die
 * richtige Antwort: `LeadsController.php:91` weist mit `422` ab und
 * `showFeedback()` sagt es dem Kunden. Ein stiller Rueckfall waere ein
 * Spam-Tor.
 */
export async function captchaTokenFor(
  cfg: Pick<CarloLeadFormConfig, 'provider' | 'siteKey'>,
  form: HTMLFormElement,
): Promise<string> {
  if (cfg.provider === 'recaptcha_v3') {
    return cfg.siteKey ? fetchRecaptchaToken(cfg.siteKey) : '';
  }

  if (cfg.provider === 'hcaptcha') {
    const field = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      HCAPTCHA_RESPONSE_SELECTOR,
    );
    return field?.value ?? '';
  }

  return '';
}

function pushConversion(payload: Record<string, unknown>): void {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event: 'carlo_conversion', ...payload });
}

function showFeedback(form: HTMLFormElement, text: string, isError: boolean): void {
  const slot = form.querySelector<HTMLElement>('.carlo-lead-form__feedback');
  if (slot) {
    slot.textContent = text;
    slot.classList.toggle('carlo-lead-form__feedback--error', isError);
    slot.classList.toggle('carlo-lead-form__feedback--success', !isError);
    slot.removeAttribute('hidden');
  }
}

function replaceWithThankYou(form: HTMLFormElement): void {
  const wrap = form.closest('.carlo-lead-form-wrap') ?? form.parentElement;
  if (wrap instanceof HTMLElement) {
    // Build the thank-you DOM with safe, text-only nodes — never innerHTML.
    while (wrap.firstChild) {
      wrap.removeChild(wrap.firstChild);
    }
    const p = document.createElement('p');
    p.className = 'carlo-lead-form__thanks';
    /**
     * 09.08.2026, Befund B11 — die Danke-Meldung muss zur Strecke passen.
     *
     * Mit eingeschalteter Bestaetigungsstrecke hat das Autohaus die Anfrage
     * noch gar nicht. „Wir melden uns binnen 24 Stunden" waere dann schlicht
     * falsch, und der Kunde wuerde die Bestaetigungsmail fuer eine
     * Empfangsbestaetigung halten und nicht klicken. Die Vorlage stellt den
     * Schalter als `data-doi` bereit; ohne das Attribut (alte Vorlage im
     * Cache) bleibt es beim bisherigen Satz.
     */
    p.textContent =
      form.dataset.doi === '1'
        ? 'Vielen Dank! Bitte bestätigen Sie Ihre Anfrage über den Link, den wir Ihnen gerade per E-Mail geschickt haben.'
        : 'Vielen Dank! Wir haben Ihre Anfrage erhalten und melden uns binnen 24 Stunden.';
    wrap.appendChild(p);
  } else {
    form.style.display = 'none';
  }
}

function buildPayload(form: HTMLFormElement): Record<string, unknown> {
  const data = new FormData(form);
  const out: Record<string, unknown> = {};
  data.forEach((value, key) => {
    if (key.endsWith('[]')) {
      const k = key.slice(0, -2);
      const arr = (out[k] as unknown[]) ?? [];
      arr.push(value);
      out[k] = arr;
    } else if (key === 'dataPrivacy' || key === 'consent') {
      out[key] = value === '1' || value === 'on' || value === 'true';
    } else if (key === 'vehicleId') {
      out[key] = Number.parseInt(String(value), 10) || 0;
    } else {
      out[key] = value;
    }
  });
  // Booleans for required checkboxes default to false if unchecked.
  out['dataPrivacy'] = out['dataPrivacy'] === true;
  out['consent'] = out['consent'] === true;
  return out;
}

const leadFormStore = {
  state: {
    feedback: '',
  },
  actions: {
    async submit(this: unknown, event: SubmitEvent): Promise<void> {
      const form = (event?.target ?? null) as HTMLFormElement | null;
      if (!form || typeof event.preventDefault !== 'function') {
        // Ohne Formular oder ohne Event uebernehmen wir nichts — dann traegt
        // der servergestuetzte Rueckfall (LeadFormPostHandler) die Anfrage.
        return;
      }
      // Erst wenn feststeht, dass wir das Absenden übernehmen können, wird der
      // native POST unterdrückt. Ohne Formular gibt es nichts zu übernehmen —
      // dann soll der servergestützte Rückfall greifen (LeadFormPostHandler).
      event.preventDefault();
      const cfg = configFor(form);

      const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
      }

      const payload = buildPayload(form);

      if (cfg.provider !== 'none') {
        const token = await captchaTokenFor(cfg, form);
        payload.captcha_token = token;
        // Der Rueckschrieb greift nur dort, wo das versteckte Feld existiert
        // (reCAPTCHA-Zweig des Partials) — er ist der Traeger fuer den
        // servergestuetzten Rueckfall aus `LeadFormPostHandler`.
        const tokenInput = form.querySelector<HTMLInputElement>('#carlo-lead-captcha-token');
        if (tokenInput) {
          tokenInput.value = token;
        }
      }

      try {
        const url = cfg.restUrl.replace(/\/$/, '') + '/leads';
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': cfg.restNonce,
          },
          body: JSON.stringify(payload),
        });

        if (response.status === 422) {
          showFeedback(form, 'Spam-Schutz hat die Anfrage abgewiesen. Bitte erneut versuchen.', true);
          return;
        }

        if (!response.ok) {
          showFeedback(form, 'Senden fehlgeschlagen. Bitte später erneut versuchen.', true);
          return;
        }

        const json = (await response.json().catch(() => ({}))) as Record<string, unknown>;
        pushConversion({
          inquiryType: payload.inquiryType,
          leadId: json.lead_id ?? json.leadId ?? null,
          vehicleId: payload.vehicleId,
        });

        replaceWithThankYou(form);
      } catch (_err) {
        showFeedback(form, 'Senden fehlgeschlagen. Bitte später erneut versuchen.', true);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
        }
      }
    },
  },
};

/**
 * 09.08.2026 — Registrierung SYNCHRON beim Auswerten des Moduls.
 *
 * Vorher stand hier ein `await import('@wordpress/interactivity')`. Der Store
 * wurde damit erst in einer späteren Mikrotask registriert — nach der
 * Hydration der Interactivity-Laufzeit. Zu diesem Zeitpunkt hatte
 * `data-wp-on--submit="actions.submit"` längst ins Leere aufgelöst, und das
 * Formular fiel auf seinen nativen POST zurück (gemessen: HTTP 404 auf der
 * Fahrzeug-URL, Anfrage verloren).
 *
 * Ein statischer Import ist auch der Weg, den `main.ts` geht, und der einzige,
 * den die Laufzeit garantiert vor der Hydration sieht: Script-Module werden in
 * Abhängigkeitsreihenfolge ausgewertet, bevor die Interactivity-Laufzeit ihre
 * Direktiven verdrahtet.
 */
registerStore(NS, leadFormStore);

export {};
